import { useState, useEffect } from "react";
import { Grid, Plus } from "lucide-react";
import { Button } from "../../components/ui/button";
import { AddProductForm } from "../../components/dashboard/shop/add-product-form";
import { ProductsTable } from "../../components/dashboard/shop/products-table";
import { ProductDetails } from "../../components/dashboard/shop/product-details";
import { ProductCategories } from "../../components/dashboard/shop/product-categories";
import { deleteProduct, getAllCategories } from "../../services/api"; // Import the API functions
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

type View = "list" | "add" | "edit" | "details" | "categories";

interface Category {
  id: string;
  name: string;
}

export default function InventoryPage() {
  const [view, setView] = useState<View>("list");
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [categories, setCategories] = useState<Category[]>([]);

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = (await getAllCategories()) as any;
      setCategories(response.data.data); // Adjust based on API response structure
    } catch (error) {
      console.error("Failed to fetch categories:", error);
      toast.error("Failed to fetch categories. Please try again.", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  // Helper function to get category title by category_id
  const getCategoryTitle = (categoryId: string) => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category ? category.name : "Uncategorized";
  };

  const handleDeleteProduct = async (productId: string) => {
    try {
      await deleteProduct(productId);
      setView("list"); // Return to the list view after deletion
      toast.success("Product deleted successfully!", {
        position: "top-center",
        autoClose: 3000,
      });
      // Optionally, you can refresh the product list here
    } catch (error) {
      console.error("Failed to delete product:", error);
    }
  };

  if (view === "add") {
    return (
      <div className="p-8">
        <AddProductForm onBack={() => setView("list")} />
      </div>
    );
  }

  if (view === "edit" && selectedProduct) {
    return (
      <div className="p-8">
        <AddProductForm
          onBack={() => setView("list")}
          editProduct={selectedProduct}
        />
      </div>
    );
  }

  if (view === "details" && selectedProduct) {
    return (
      <div className="p-8">
        <ProductDetails
          product={{
            ...selectedProduct,
            openingStock: 20,
            remainingStock: 14,
            purchased: 6,
          }}
          // categories={categories} // Pass categories as a prop
          getCategoryTitle={getCategoryTitle} // Pass getCategoryTitle as a prop
          onBack={() => setView("list")}
          onEdit={() => {
            setView("edit");
          }}
          onDelete={() => {
            handleDeleteProduct(selectedProduct.id);
          }}
        />
      </div>
    );
  }

  if (view === "categories") {
    return (
      <div className="p-8">
        <ProductCategories onBack={() => setView("list")} />
      </div>
    );
  }

  return (
    <div className="p-8">
      <ToastContainer />
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold mb-1">Inventory</h1>
          <p className="text-muted-foreground">
            Manage and track your product inventory
          </p>
        </div>
        <div className="flex gap-4">
          <Button
            variant="outline"
            className="border-[#B87775] text-[#B87775] hover:bg-[#B87775]/10"
            onClick={() => setView("categories")}
          >
            <Grid className="mr-2 h-4 w-4" />
            CATEGORIES
          </Button>
          <Button
            className="bg-[#B87775] hover:bg-[#B87775]/90 text-white"
            onClick={() => setView("add")}
          >
            <Plus className="mr-2 h-4 w-4" />
            ADD PRODUCT
          </Button>
        </div>
      </div>

      <ProductsTable
        // categories={categories} // Pass categories as a prop
        getCategoryTitle={getCategoryTitle} // Pass getCategoryTitle as a prop
        onView={(product) => {
          setSelectedProduct(product);
          setView("details");
        }}
        onEdit={(product) => {
          setSelectedProduct(product);
          setView("edit");
        }}
        onDelete={(product) => {
          handleDeleteProduct(product.id);
        }}
      />
    </div>
  );
}
